import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { pipe, filter, propEq, map } from 'ramda'
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { breakpoints, colors, fontWeights } from '@/styles'
import { show as showEndTenancyModal } from '@/features/EndTenancy/end-tenancy-modal'
import config from '@/config'
import { select } from '@/store'
import { showEditNameModal } from '@/pages/Contract/CustomContractCreate/add-documents/edit-name-modal'
import { default as checkIsMobile } from '@/utils/is-mobile'
import { handlePDFView, handlePDFDownload } from './pdf-view'
import RecipientsMenu from './recipients-menu'
import { showDocumentModal } from '../modal'
import MoreIcon from '../more-icon.svg'
import MobileCloseIcon from '../mobile-close-icon.svg'
import EditContractIcon from '../dropdown-icons/edit-contract-icon.svg?react'
import EditNameIcon from '../dropdown-icons/edit-name-icon.svg?react'
import ViewIcon from '../dropdown-icons/view-icon.svg?react'
import DownloadIcon from '../dropdown-icons/download-icon.svg?react'
import HistoryIcon from '../dropdown-icons/history-icon.svg?react'
import EndTenancyIcon from '../dropdown-icons/end-tenancy-icon.svg?react'
import DeleteIcon from '../dropdown-icons/delete-icon.svg?react'
import ResendIcon from '../dropdown-icons/resend-icon.svg?react'
import VoidIcon from '../dropdown-icons/void-icon.svg?react'

const DropdownWrapper = styled.div`
  position: relative;
`

const Image = styled.img`
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  :hover {
    opacity: 0.5;
  }
`

const DropdownMenuWrapper = styled.span`
  position: absolute;
  width: max-content;
  background: ${colors.white};
  border-radius: 8px;
  padding: 4px;
  box-shadow:
    0px 10px 15px -3px rgba(0, 0, 0, 0.1),
    0px 4px 6px -2px rgba(0, 0, 0, 0.05);
  top: 40px;
  left: -120px;
  z-index: 2;
  ${props =>
    props.isMobile
      ? 'position: fixed; width: 100%; top: inherit; left: 0; bottom: 0; border-radius: 16px 16px 0px 0px; padding:24px; '
      : ''}
`
const DropdownMenuWrapperOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  background-color: black;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1;
  opacity: 0.3;
`
const DropdownMenuCloseButton = styled.img`
  position: absolute;
  z-index: 2;
  right: 10px;
  top: -70px;
`
const DropdownMenuItem = styled.span`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 4px auto;
  padding: 10px 9px;
  border-radius: 8px;
  background-color: ${colors.white};
  cursor: pointer;
  color: ${props => props.color};

  &:hover,
  &:active {
    background-color: ${colors.grey96};
  }

  &:active {
    color: ${props => (props.color ? props.color : `${colors.white}`)};
  }
  &:disabled,
  &[disabled] {
    opacity: 0.3;
    cursor: not-allowed;
    user-select: none;
    pointer-events: none;
  }
  svg {
    margin-right: 10px;
  }

  strong {
    font-weight: ${fontWeights.medium};
    color: ${colors.link};
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 16px;
    font-weight: 500;
    color: ${colors.black};
    margin: 0;
    padding: 16px;
    border-bottom: 1px solid #ddd;
    border-radius: 0;

    &:last-child {
      border-bottom: 0;
    }
  }
`
const CountdownContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: flex-end;
  width: 30px;
  height: 30px;
  font-size: 11px;
`

const color = colors.grey56
const redColor = colors.red64

const handleMouseDown = e => {
  const childColor = e.target.children[0].getAttribute('fill')
  if (childColor === redColor) return
  e.target.children[0].style.fill = colors.black
}
const handleMouseUp = e => {
  const childColor = e.target.children[0].getAttribute('fill')
  if (childColor === redColor) return
  e.target.children[0].style.fill = color
}

const DropdownMenu = ({
  isMobile = false,
  isNativeContract,
  viewContract,
  contract,
  packageFile,
  packageFiles,
  countDown,
  countDownLimit,
  canResend,
  setCanResend,
  handleContractEdit
}) => {
  const [visible, setVisible] = useState(false)
  const [generatingDownloadState, setGeneratingDownloadState] = useState('none')
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const node = useRef(null)

  const isContractDraft = packageFile?.state_machine === 'draft'
  const isContractError = packageFile?.state_machine === 'error'
  const isContractUnsent = packageFile?.state_machine === 'created'
  const isContractSentNotSigned =
    packageFile?.state_machine === 'sent' && packageFile?.signature_status === 'pending'
  const isContractSigned = packageFile?.state_machine === 'signed'
  const downloadContractUrl = `${config.BACKEND_SERVER}/contracts/${contract?.id}/download/package/${packageFile.id}`

  const handleContractDownloadMobile = () => {
    if (generatingDownloadState === 'none') {
      checkIfDownloadIsReady()
    } else if (generatingDownloadState === 'done') {
      window.open(downloadContractUrl)
      setGeneratingDownloadState('none')
    }
  }

  const handleContractDownload = async () => {
    const response = await handlePDFDownload(contract?.id, packageFile.id)

    if (response.ok) {
      window.open(downloadContractUrl)
    }
  }

  const checkIfDownloadIsReady = async () => {
    setGeneratingDownloadState('generating')
    const response = await handlePDFDownload(contract?.id, packageFile.id)

    if (response.ok) {
      setGeneratingDownloadState('done')
    }
  }

  const handleContractView = e => {
    e.stopPropagation()
    handlePDFView(contract?.id, packageFile.id)
  }

  const handleNativeContractDownload = e => {
    e.stopPropagation()
    const docName = contract.state_machine === 'draft' ? 'draft' : 'contract'
    window.open(
      `${config.BACKEND_SERVER}/dynamic_contracts/view/contract/${contract.id}/${docName}.pdf`
    )
  }

  const handleClick = event => {
    if (typeof node === 'undefined' || node?.current?.contains(event.target)) {
      return
    }
    setVisible(false)
  }

  const handleEditContractName = async e => {
    e.stopPropagation()
    showEditNameModal({ packageFile, contractId: contract?.id, dispatch })
  }

  const handleTenancyAgreementDelete = async () => {
    await dispatch.contract.cancelCustomContract({
      contractId: contract?.id
    })
  }
  const handleTenancyAgreementVoid = async fileId => {
    const { response } = await dispatch.contract.removeTenancyAgreement({
      contractId: contract?.id,
      fileId
    })
    return response?.ok
  }

  const handlePackageFileDelete = async id => {
    const fileId = id ? id : packageFile?.id
    const { response } = await dispatch.contract.removePackageFile({
      contractId: contract?.id,
      fileId: fileId
    })
    return response?.ok
  }

  const handleVoidTenancyAgreement = async () => {
    const sentDocuments = pipe(filter(propEq('state_machine', 'sent')))(packageFiles)

    const pageRequests = pipe(
      map(async value => {
        const voidFunction =
          value?.document_type === 'tenancy_agreement'
            ? handleTenancyAgreementVoid
            : handlePackageFileDelete
        await voidFunction(value?.id)
      })
    )(sentDocuments)

    await Promise.all(pageRequests)
  }

  const handleContractDelete = e => {
    e.stopPropagation()
    if (packageFile?.document_type === 'tenancy_agreement') {
      showDocumentModal({
        title: 'Are you sure you want to delete all documents?',
        content:
          'By deleting the Tenancy Agreement, all progress will be lost and any supplementary documents will also be deleted.',
        confirmCta: 'Delete',
        cancelCta: 'Cancel',
        backgroundColor: colors.red96,
        confirmOnClick: handleTenancyAgreementDelete,
        confirmTheme: 'delete',
        cancelTheme: 'secondary',
        confirmCtaIcon: 'trash'
      })
    } else {
      showDocumentModal({
        title: 'Are you sure you want to delete this document?',
        content:
          'If you continue, all progress on this document will be deleted. You will need to reupload this document and start again.',
        confirmCta: 'Delete',
        cancelCta: 'Cancel',
        backgroundColor: colors.red96,
        confirmOnClick: handlePackageFileDelete,
        confirmTheme: 'delete',
        cancelTheme: 'secondary',
        confirmCtaIcon: 'trash'
      })
    }

    setVisible(false)
  }

  const handleContractWithdraw = e => {
    e.stopPropagation()
    if (packageFile?.document_type === 'tenancy_agreement') {
      showDocumentModal({
        title: 'Are you sure you want to void all documents?',
        content:
          'By voiding the Tenancy Agreement, you are canceling all remaining signing activities. You will be able to edit any previously signed documents, but you will need to resend the updated document back to the recipient(s) to be signed.',
        confirmCta: 'Void',
        cancelCta: 'Cancel',
        backgroundColor: colors.red96,
        confirmOnClick: handleVoidTenancyAgreement,
        confirmTheme: 'delete',
        cancelTheme: 'secondary',
        confirmCtaIcon: 'trash'
      })
    } else {
      showDocumentModal({
        title: 'Are you sure you want to void this document?',
        content:
          'By voiding this document, you will be able to edit this document, but you will need to resend this document back to the recipient(s) to be signed.',
        confirmCta: 'Void',
        cancelCta: 'Cancel',
        backgroundColor: colors.red96,
        confirmOnClick: handlePackageFileDelete,
        confirmTheme: 'delete',
        cancelTheme: 'secondary',
        confirmCtaIcon: 'trash'
      })
    }

    setVisible(false)
  }

  const handleResend = async e => {
    e.stopPropagation()
    if (canResend) {
      await dispatch.contract.sendCustomDocument({
        contractId: contract?.id,
        packageFileId: packageFile?.id
      })
    }
    setCanResend(false)
  }

  const endTenancyRequestData = useSelector(state =>
    select.endTenancy.endTenancyRequestData(state, { listingId: contract?.listing_id })
  )

  const endTenancyInProgress = endTenancyRequestData?.state_machine === 'active'

  const endTenancyDone =
    endTenancyRequestData?.state_machine === 'done' &&
    endTenancyRequestData?.end_response === 'accepted'

  const canEndTenancy = !endTenancyInProgress && !endTenancyDone

  const getDownloadText = () => {
    if (generatingDownloadState === 'none')
      return t('l.contract_manager.documents.download.button', 'Download')
    else if (generatingDownloadState === 'generating')
      return t('l.contract_manager.documents.generating_document.button', 'Generating document...')
    else if (generatingDownloadState === 'done')
      return (
        <span>
          {t('l.contract_manager.documents.document_ready.button', 'Document ready!')}&nbsp;
          <strong>{t('l.contract_manager.documents.download_file.button', 'Download File')}</strong>
        </span>
      )
  }

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])

  const BUTTONS = {
    download: (
      <DropdownMenuItem
        onClick={checkIsMobile() ? handleContractDownloadMobile : handleContractDownload}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <DownloadIcon fill={color} />{' '}
        {checkIsMobile()
          ? getDownloadText()
          : t('l.contract_manager.documents.download.button', 'Download')}
      </DropdownMenuItem>
    ),
    native_download: (
      <DropdownMenuItem
        onClick={handleNativeContractDownload}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <DownloadIcon fill={color} />{' '}
        {t('l.contract_manager.documents.download.button', 'Download')}
      </DropdownMenuItem>
    ),
    history: (
      <DropdownMenuItem onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
        <HistoryIcon fill={color} /> {t('l.contract_manager.documents.history.button', 'History')}
      </DropdownMenuItem>
    ),
    edit_contract: (
      <DropdownMenuItem
        onClick={e => handleContractEdit(e, packageFile)}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <EditContractIcon fill={color} />
        {t('l.contract_manager.documents.edit_contract.button', 'Edit Contract')}
      </DropdownMenuItem>
    ),
    edit_name: (
      <DropdownMenuItem
        onClick={handleEditContractName}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        disabled={isContractSentNotSigned}
      >
        <EditNameIcon fill={color} />
        {t('l.contract_manager.documents.edit_name.button', 'Edit Name')}
      </DropdownMenuItem>
    ),
    delete: (
      <DropdownMenuItem
        color="red"
        onClick={handleContractDelete}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <DeleteIcon fill={redColor} /> {t('common.delete', 'delete')}
      </DropdownMenuItem>
    ),
    void: (
      <DropdownMenuItem
        color="red"
        onClick={handleContractWithdraw}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <VoidIcon fill={redColor} />
        {t('l.contract_manager.documents.void_documents.button', 'Void Document')}
      </DropdownMenuItem>
    ),
    void_all: (
      <DropdownMenuItem
        color="red"
        onClick={handleContractWithdraw}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <VoidIcon fill={redColor} />
        {t('l.contract_manager.documents.void_all_documents.button', 'Void All Documents')}
      </DropdownMenuItem>
    ),
    view: (
      <DropdownMenuItem
        onClick={handleContractView}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <EditContractIcon fill={color} /> {t('l.contract_manager.documents.view.button', 'View')}
      </DropdownMenuItem>
    ),
    end_tenancy: (
      <DropdownMenuItem
        onClick={() => showEndTenancyModal({ listingId: contract?.listing_id })}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        <EndTenancyIcon fill={redColor} />
        {t('l.contract_manager.documents.end_tenancy.button', 'End Tenancy')}
      </DropdownMenuItem>
    ),
    resend: (
      <DropdownMenuItem
        onClick={handleResend}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        disabled={!canResend}
        style={{
          display: 'flex',
          gap: '6px',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <span>
          <ResendIcon fill={color} />
          {t('l.contract_manager.documents.resend.button', 'Resend')}
        </span>

        {!canResend && (
          <CountdownContainer>
            <CircularProgressbarWithChildren
              value={countDown}
              minValue={0}
              maxValue={countDownLimit}
              strokeWidth={5}
              counterClockwise={false}
              styles={buildStyles({
                pathColor: `${colors.black}`,
                textColor: `${colors.black}`,
                pathTransitionDuration: 0.5
              })}
            >
              {countDown}
            </CircularProgressbarWithChildren>
          </CountdownContainer>
        )}
      </DropdownMenuItem>
    ),
    participants: (
      <RecipientsMenu
        viewContract={viewContract}
        isNativeContract={isNativeContract}
        packageFile={packageFile}
        contract={contract}
        packageFiles={packageFiles}
      />
    )
  }

  const renderNativeContractMenu = () => {
    return (
      <>
        <DropdownMenuItem
          onClick={viewContract}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
        >
          <ViewIcon fill={color} /> {t('l.contract_manager.documents.view.button', 'View')}
        </DropdownMenuItem>
        {BUTTONS['native_download']}
        {/* {BUTTONS['history']} */}
        {contract?.state_machine === 'active' && canEndTenancy && BUTTONS['end_tenancy']}
      </>
    )
  }

  const renderUnsentContract = () => {
    return (
      <>
        {BUTTONS['edit_contract']}
        {BUTTONS['edit_name']}
        {BUTTONS['download']}
        {/* {BUTTONS['history']} */}
        {isMobile && BUTTONS['participants']}
        {BUTTONS['delete']}
      </>
    )
  }

  const renderDraftContract = () => {
    return (
      <>
        {BUTTONS['edit_contract']}
        {BUTTONS['edit_name']}
        {isMobile && BUTTONS['participants']}
        {BUTTONS['delete']}
      </>
    )
  }

  const renderSentContract = () => {
    const voidAction =
      packageFile?.document_type === 'tenancy_agreement' ? BUTTONS['void_all'] : BUTTONS['void']
    return (
      <>
        {BUTTONS['view']}
        {BUTTONS['resend']}
        {BUTTONS['edit_name']}
        {isMobile && BUTTONS['participants']}

        {/* {BUTTONS['history']} */}
        {voidAction}
      </>
    )
  }

  const renderSignedContract = () => {
    return (
      <>
        {BUTTONS['download']}
        {/* {BUTTONS['history']} */}
        {packageFile?.document_type === 'tenancy_agreement' &&
          !endTenancyDone &&
          BUTTONS['end_tenancy']}
        {isMobile && BUTTONS['participants']}
      </>
    )
  }

  const renderDropdown = () => {
    if (isNativeContract) return renderNativeContractMenu()
    if (isContractDraft || isContractError) return renderDraftContract()
    if (isContractUnsent) return renderUnsentContract()
    if (isContractSentNotSigned) return renderSentContract()
    if (isContractSigned) return renderSignedContract()
  }

  return (
    <DropdownWrapper ref={node}>
      <Image
        src={MoreIcon}
        alt="more"
        onClick={e => {
          e.stopPropagation()
          setVisible(!visible)
        }}
      />
      {visible && (
        <>
          <DropdownMenuWrapper isMobile={isMobile}>
            {isMobile && (
              <DropdownMenuCloseButton
                src={MobileCloseIcon}
                alt="icon"
                onClick={e => {
                  e.stopPropagation()
                  setVisible(!visible)
                }}
              />
            )}
            {renderDropdown()}
          </DropdownMenuWrapper>
          {isMobile && (
            <DropdownMenuWrapperOverlay
              onClick={e => {
                e.stopPropagation()
              }}
            />
          )}
        </>
      )}
    </DropdownWrapper>
  )
}

export default DropdownMenu
